/**
 * Shopify Storefront API — barrel export
 *
 * All imports from '@/lib/shopify' still work after refactor.
 * Internals split into client.ts, queries.ts, and cart.ts.
 */

// Re-export client
export { storefrontApiRequest } from './shopify/client';

// Re-export cart functions & mutations
export {
  CART_QUERY,
  CART_CREATE_MUTATION,
  CART_LINES_ADD_MUTATION,
  CART_LINES_UPDATE_MUTATION,
  CART_LINES_REMOVE_MUTATION,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
} from './shopify/cart';

// Re-export queries
export { PRODUCTS_QUERY, PRODUCT_BY_HANDLE_QUERY } from './shopify/queries';

// ── Types ──────────────────────────────────────────────

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    descriptionHtml: string;
    handle: string;
    productType: string;
    tags: string[];
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

// ── Product Fetching ───────────────────────────────────

import { storefrontApiRequest as _request } from './shopify/client';
import { PRODUCTS_QUERY as _PQ, PRODUCT_BY_HANDLE_QUERY as _PBHQ } from './shopify/queries';

export async function fetchProducts(first = 50, query?: string): Promise<ShopifyProduct[]> {
  const data = await _request(_PQ, { first, query: query || null });
  return data?.data?.products?.edges || [];
}

export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct['node'] | null> {
  const data = await _request(_PBHQ, { handle });
  return data?.data?.product || null;
}

// ── Price Helpers ──────────────────────────────────────

export function formatPrice(amount: string, currencyCode: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(parseFloat(amount));
}
